/* What each column of this dataset actually looks like, and how it moved.
 *
 * Rendered inside `DatasetDetail` rather than as its own route, because a profile is *about* a dataset
 * and a separate screen would need a dataset picker duplicating the catalog. The slice is its own, so
 * the profiler's logic stays out of the catalog feature.
 *
 * **The design's layout: the columns on the left, what changed and the figures at a glance on the
 * right.** Each column row opens to show its frequent-value shares and the counts behind its
 * completeness, so the table stays one line per column — which is what a reader matches against their
 * own DDL — and the detail is one click away rather than six more columns wide.
 *
 * **Every figure says whether it was measured.** `exact` travels on every row for the reason the
 * contract gives: the same 3% movement is a finding when measured and noise inside the estimator's
 * error bar. A theta sketch at k=1024 carries ~3% relative standard error, so presenting an estimate
 * as a measurement invites somebody to act on the error bar.
 *
 * **A null is "not measured", never a zero.** `completeness` is null for an *empty relation* and a
 * measured 0.0 for an all-null column — two facts the API deliberately separates. The bar shows the
 * difference as the design draws it: a hatched track where nothing was measured, an empty plain track
 * where a zero was.
 *
 * **The profile button's bounds.** A profile is up to 20 full `GROUP BY` scans of a customer's
 * production table, and S-25 records a double-clicked button buying 40 of them. The control is
 * `disabled` while a request is in flight, `useRequestProfile` holds one key per dataset and clears it
 * only on success, and the cost is on the control's own title. The progress readout follows *any*
 * profile request for this dataset — the page header's button included — because both are the same
 * mutation key, so the readout cannot miss a run because a different button started it.
 *
 * **No value is shown anywhere, and nothing here renders a digest.** The API returns salted digests
 * and counts; the frequent values are shown by *rank and share* only. A salted digest is rung 3, so
 * showing one would be a design question rather than a disclosure defect; not showing one is still the
 * right answer, because a digest tells a reader nothing they can act on.
 */

import { useIsMutating, useMutationState } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type {
  ColumnDrift,
  ColumnSketch,
  DatasetSketches,
  SketchDrift,
} from '@/shared/api/generated/catalog';
import { ageText } from '@/shared/model/age';
import { useEmbedded } from '@/shared/embedded';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { HelpLabel } from '@/shared/ui/InfoTip';

import {
  profileMutationKey,
  useRequestProfile,
  useSketchDrift,
  useSketches,
} from '../api/profiler';
import {
  DRIFT_TONE,
  count,
  distinctReading,
  dominantReading,
  driftVerdict,
  isDominated,
  share,
} from '../model/presentation';
import type { DriftVerdict } from '../model/presentation';
import { GlanceSummary } from './GlanceSummary';

export interface ProfilePanelProps {
  readonly datasetId: string;
  /** Columns in the current schema version, or `undefined` when the caller does not know.
   *
   * Only used to decide when a running profile has finished. `undefined` disables polling
   * altogether, which is the honest default: without a total there is no way to say a profile is
   * complete, and an interval that cannot end is worse than no progress at all. */
  readonly expectedColumns?: number | undefined;
  /** Schema version numbers by version id, so the changes card can name the version it compares
   * against. Absent, it says "the previous schema version" without a number. */
  readonly versionNumbers?: ReadonlyMap<string, number> | undefined;
  /** Open Profile history. Absent, the changes card offers no link to it. */
  readonly onCompareRuns?: (() => void) | undefined;
}

/** Completeness below this reads in the warning hue on the bar, as the design draws it. */
const COMPLETENESS_WATCH = 0.9;
/** A completeness movement smaller than this, in points, is not worth an arrow. */
const DELTA_FLOOR_POINTS = 0.05;

export function ProfilePanel({
  datasetId,
  expectedColumns,
  versionNumbers,
  onCompareRuns,
}: ProfilePanelProps): ReactElement {
  const { t } = useTranslation();
  const sketches = useSketches(datasetId, expectedColumns);
  const drift = useSketchDrift(datasetId);
  const requestProfile = useRequestProfile(datasetId);
  const [openColumn, setOpenColumn] = useState<string | null>(null);

  /* Any profile request for this dataset, from this panel or from the page header. */
  const requested =
    useMutationState({
      filters: { mutationKey: profileMutationKey(datasetId), status: 'success' },
    }).length > 0;
  const inFlight = useIsMutating({ mutationKey: profileMutationKey(datasetId) }) > 0;

  /* How many columns are durable right now, and how many a complete profile would have. Both come
     from data already on the page — no operation table, no synthetic percentage. */
  const stored = sketches.data?.columns.length ?? 0;
  const progress =
    expectedColumns === undefined || expectedColumns <= 0
      ? 0
      : Math.min(1, stored / expectedColumns);
  /* Shown while a request has been made and the count is short, so it ends on exactly the condition
     the poll ends on — not "while the mutation is pending", which is a 202 that returns in
     milliseconds and would vanish before the first column was scanned. */
  const profiling =
    requested && expectedColumns !== undefined && stored < expectedColumns;

  if (sketches.isPending) {
    return (
      <div className="lq-pp__loading" role="status" aria-label={t('profile.loading')}>
        <span className="lq-skeleton__bar lq-pp__skeletonHead" />
        <span className="lq-skeleton__bar lq-pp__skeletonBody" />
      </div>
    );
  }

  if (sketches.isError) {
    return <Alert tone="error">{sketches.error.message}</Alert>;
  }

  const progressReadout = profiling ? (
    /* **Progress, and every part of it is a count rather than a clock.** `capture` appends and commits
       one column at a time, so `stored` genuinely rises as scans finish; a bar that advanced on a timer
       would tell a reader a profile was progressing while it sat blocked on a warehouse. */
    <div className="lq-profiling" role="status">
      <div className="lq-profiling__head">
        <span>{t('profile.runningDetail')}</span>
        <span className="lq-pp__num">
          {t('profile.runningCount', { stored, total: expectedColumns })}
        </span>
      </div>
      <div className="lq-profiling__track">
        <div
          className="lq-profiling__fill"
          style={{ width: `${String(Math.round(progress * 100))}%` }}
        />
      </div>
    </div>
  ) : null;

  if (sketches.data === null) {
    return (
      <div className="lq-pp">
        {requestProfile.isError && (
          <Alert tone="error">{requestProfile.error.message}</Alert>
        )}
        {progressReadout}
        {/* **"No profile for the current schema version", not "not profiled yet".** `sketches`
            reads the dataset's *latest* version, so a table profiled before its schema last changed
            answers `null` here while the dataset listing — which reports the most recent profile of
            any version — still shows a date. Both are correct; the wording says which. */}
        <div className="lq-pp__empty">
          <span className="lq-pp__emptyMark" aria-hidden="true" />
          <h3 className="lq-pp__emptyTitle">{t('profile.never.title')}</h3>
          <p className="lq-pp__emptyBody">{t('profile.never.guidance')}</p>
          <Button
            tone="primary"
            busy={inFlight}
            disabled={inFlight}
            title={t('profile.runCost')}
            onClick={() => {
              requestProfile.mutate();
            }}
          >
            {inFlight ? t('profile.running') : t('profile.run')}
          </Button>
        </div>
      </div>
    );
  }

  /* **Read from `isSuccess`, not from `data`, and that distinction was found by forcing a 500.**
   * React Query keeps the previous `data` alongside `isError` by design, so building the map from
   * `drift.data` rendered confident verdicts *underneath* a banner saying the comparison could not
   * be read. Every row reads "not compared" when the comparison failed. */
  const driftByColumn = new Map<string, ColumnDrift>(
    (drift.isSuccess ? (drift.data?.columns ?? []) : []).map((row) => [row.column, row]),
  );

  return (
    <div className="lq-pp">
      {requestProfile.isError && (
        <Alert tone="error">{requestProfile.error.message}</Alert>
      )}
      {progressReadout}
      <GlanceSummary
        columns={sketches.data.columns}
        drift={drift.isSuccess ? (drift.data?.columns ?? null) : null}
      />
      <div className="lq-pp__layout">
        <div className="lq-pp__main">
          <ColumnsCard
            sketches={sketches.data}
            driftByColumn={driftByColumn}
            openColumn={openColumn}
            onToggle={(column) => {
              setOpenColumn(openColumn === column ? null : column);
            }}
          />
        </div>
        <aside className="lq-pp__side">
          <ChangesCard
            drift={drift}
            versionNumbers={versionNumbers}
            onCompareRuns={onCompareRuns}
            onOpen={(column) => {
              setOpenColumn(column);
            }}
          />
          <GlanceCard sketches={sketches.data} />
        </aside>
      </div>
    </div>
  );
}

function ColumnsCard({
  sketches,
  driftByColumn,
  openColumn,
  onToggle,
}: {
  readonly sketches: DatasetSketches;
  readonly driftByColumn: ReadonlyMap<string, ColumnDrift>;
  readonly openColumn: string | null;
  readonly onToggle: (column: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const scanned = sketches.columns[0]?.rows_scanned ?? null;
  return (
    <section
      className="lq-pp__card lq-pp__card--flush"
      aria-labelledby="profile-columns-heading"
    >
      <div className="lq-pp__cardHead">
        {/* What profiling is, on the heading of the table that is the profile — the tab has no
            title of its own for it to sit beside. */}
        <HelpLabel
          subject={t('profile.columns.title')}
          help={t('help.feature.profiling')}
        >
          <h3 id="profile-columns-heading" className="lq-pp__cardTitle">
            {t('profile.columns.title')}
          </h3>
        </HelpLabel>
        <span className="lq-pp__cardMeta">
          {t('profile.columns.meta', {
            at: ageText(t, sketches.computed_at, new Date()) ?? sketches.computed_at,
            columns: sketches.columns.length,
            rows: count(scanned),
          })}
        </span>
      </div>
      <div className="lq-pp__scroll">
        <table className="lq-pp__table">
          <caption className="lq-visually-hidden">{t('profile.caption')}</caption>
          <thead>
            <tr>
              <td className="lq-pp__chevCell" aria-hidden="true" />
              <th scope="col">{t('profile.column.name')}</th>
              <th scope="col">
                <HelpLabel
                  subject={t('profile.column.complete')}
                  help={t('help.metric.complete')}
                >
                  <span>{t('profile.column.complete')}</span>
                </HelpLabel>
              </th>
              <th scope="col">
                <HelpLabel
                  subject={t('profile.column.distinct')}
                  help={t('help.metric.distinct')}
                >
                  <span>{t('profile.column.distinct')}</span>
                </HelpLabel>
              </th>
              <th scope="col">
                <HelpLabel
                  subject={t('profile.column.dominant')}
                  help={t('help.metric.topValue')}
                >
                  <span>{t('profile.column.dominant')}</span>
                </HelpLabel>
              </th>
              <th scope="col">
                <HelpLabel
                  subject={t('profile.column.drift')}
                  help={t('help.metric.drift')}
                >
                  <span>{t('profile.column.drift')}</span>
                </HelpLabel>
              </th>
            </tr>
          </thead>
          <tbody>
            {sketches.columns.map((sketch) => (
              <ColumnRows
                key={sketch.column}
                sketch={sketch}
                drift={driftByColumn.get(sketch.column)}
                open={openColumn === sketch.column}
                onToggle={() => {
                  onToggle(sketch.column);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ColumnRows({
  sketch,
  drift,
  open,
  onToggle,
}: {
  readonly sketch: ColumnSketch;
  readonly drift: ColumnDrift | undefined;
  readonly open: boolean;
  readonly onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const distinct = distinctReading(sketch);
  const dominant = dominantReading(sketch);
  const verdict = drift === undefined ? null : driftVerdict(drift);
  const detailId = useId();
  const nameId = useId();
  const tipId = useId();
  const dominated = isDominated(sketch);
  const flagRef = useRef<HTMLSpanElement | null>(null);
  /* Where to draw the tooltip, in viewport coordinates, while the row is hovered or focused. A fixed
     overlay rather than a positioned child, because the table scrolls sideways and its scroll area
     clips anything drawn outside it — the first version was invisible on every row. */
  const [tipAt, setTipAt] = useState<{ readonly x: number; readonly y: number } | null>(
    null,
  );
  /* Inside the Suite shell a portal goes to the container the shell provides inside Lumiq's root,
     never to `document.body`, which is the shell's page and outside Lumiq's styles. */
  const portalTarget = useEmbedded()?.portalContainer ?? document.body;
  const showTip = (): void => {
    const rect = flagRef.current?.getBoundingClientRect();
    if (rect !== undefined) setTipAt({ x: rect.left + rect.width / 2, y: rect.top });
  };
  const hideTip = (): void => {
    setTipAt(null);
  };
  const tipText = dominated
    ? t('profile.dominated.tip', { share: share(sketch.dominant_share) })
    : '';
  const before = drift?.completeness_before ?? null;
  const after = drift?.completeness_after ?? null;
  const delta = before === null || after === null ? null : (after - before) * 100;

  return (
    <>
      {/* **The row opens the detail, wherever it is clicked** — the name, the arrow, a figure. The
          handler is on the row rather than on a stretched overlay, which only works where a browser
          lets a table row contain positioned content. The button inside it stays the control for a
          keyboard and a screen reader: Enter or Space on it fires a click that bubbles here, so it
          has no handler of its own and a pointer click toggles exactly once. */}
      <tr
        className={open ? 'lq-pp__row lq-pp__row--open' : 'lq-pp__row'}
        onClick={onToggle}
      >
        <td className="lq-pp__chevCell" aria-hidden="true">
          <span className="lq-pp__chevron">▶</span>
        </td>
        <th scope="row">
          <button
            type="button"
            className="lq-pp__toggle"
            aria-expanded={open}
            aria-controls={detailId}
            aria-labelledby={nameId}
            aria-describedby={dominated ? tipId : undefined}
            onMouseEnter={dominated ? showTip : undefined}
            onMouseLeave={dominated ? hideTip : undefined}
            onFocus={dominated ? showTip : undefined}
            onBlur={dominated ? hideTip : undefined}
          >
            <span id={nameId} className="lq-pp__name">
              {sketch.column}
            </span>
            {dominated ? (
              /* Flagged rather than sorted to the top: the table's order is the schema's order,
                 which is what a reader is matching against their own DDL. The reason is a tooltip
                 drawn on hover and on keyboard focus of the row's button, in plain words with the
                 real share — a native `title` appeared late or not at all, and said it in jargon. */
              <span className="lq-pp__flag" ref={flagRef}>
                <span aria-hidden="true">⚠</span>
                {/* The description a screen reader hears; the drawn tooltip repeats it for the eye. */}
                <span id={tipId} className="lq-visually-hidden">
                  {tipText}
                </span>
              </span>
            ) : null}
          </button>
          {tipAt === null
            ? null
            : createPortal(
                <span
                  className="lq-pp__tip"
                  role="tooltip"
                  aria-hidden="true"
                  style={{ left: tipAt.x, top: tipAt.y }}
                >
                  {tipText}
                </span>,
                portalTarget,
              )}
        </th>
        <td>
          <span className="lq-pp__complete" title={t('profile.complete.hint')}>
            <span
              className="lq-pp__track"
              data-measured={sketch.completeness === null ? 'false' : 'true'}
              aria-hidden="true"
            >
              {sketch.completeness === null ? null : (
                <span
                  className="lq-pp__fill"
                  data-watch={
                    sketch.completeness < COMPLETENESS_WATCH ? 'true' : undefined
                  }
                  style={{ width: `${(sketch.completeness * 100).toFixed(1)}%` }}
                />
              )}
            </span>
            <span
              className={
                sketch.completeness === null
                  ? 'lq-pp__completeText lq-muted'
                  : 'lq-pp__completeText lq-pp__num'
              }
            >
              {sketch.completeness === null
                ? t('profile.notMeasured')
                : share(sketch.completeness)}
            </span>
          </span>
        </td>
        <td className="lq-pp__fig lq-pp__num">
          {/* `≈` is already in an estimated value, so the reason rides on the title rather than a
              second marker: a 3% error bar is the difference between a finding and noise. */}
          <span title={distinct.measured ? undefined : t('profile.distinct.estimated')}>
            {distinct.value}
          </span>
        </td>
        <td className="lq-pp__fig lq-pp__num">
          {dominant.value}
          {dominant.measured || sketch.dominant_share === null ? null : (
            <span className="lq-muted" title={t('profile.dominant.bounded')}>
              {' '}
              ~
            </span>
          )}
        </td>
        <td>
          {verdict === null || drift === undefined ? (
            <span className="lq-muted">{t('profile.drift.notCompared')}</span>
          ) : (
            <span className="lq-pp__drift">
              <DriftChip verdict={verdict} estimated={!drift.exact} />
              {delta !== null && Math.abs(delta) >= DELTA_FLOOR_POINTS ? (
                <span className="lq-pp__delta" data-direction={delta < 0 ? 'down' : 'up'}>
                  {t(delta < 0 ? 'profile.delta.down' : 'profile.delta.up', {
                    points: Math.abs(delta).toFixed(1),
                  })}
                </span>
              ) : null}
            </span>
          )}
        </td>
      </tr>
      {open ? (
        <tr className="lq-pp__detailRow" id={detailId}>
          <td colSpan={6}>
            <div className="lq-pp__detail">
              <div className="lq-pp__freq">
                <div className="lq-pp__detailLabel">{t('profile.freq.title')}</div>
                {sketch.frequent_values.length === 0 ? (
                  <p className="lq-pp__detailNote">{t('profile.freq.none')}</p>
                ) : (
                  sketch.frequent_values.slice(0, 5).map((value, index) => (
                    <div className="lq-pp__freqRow" key={value.digest}>
                      <span className="lq-pp__freqRank">
                        {t('profile.freq.rank', { rank: index + 1 })}
                      </span>
                      <span className="lq-pp__freqTrack" aria-hidden="true">
                        <span
                          className="lq-pp__freqFill"
                          style={{
                            width: `${String(Math.max(2, Math.min(100, value.share * 100)))}%`,
                          }}
                        />
                      </span>
                      <span className="lq-pp__freqShare lq-pp__num">
                        {share(value.share)}
                      </span>
                    </div>
                  ))
                )}
                {sketch.frequent_values_truncated ? (
                  <p className="lq-pp__detailNote">{t('profile.freq.truncated')}</p>
                ) : null}
              </div>
              <dl className="lq-pp__facts">
                <dt>{t('profile.detail.change')}</dt>
                <dd>
                  {before === null || after === null
                    ? t('profile.detail.noPrevious')
                    : t('profile.detail.changeValue', {
                        before: share(before),
                        after: share(after),
                      })}
                </dd>
                <dt>{t('profile.column.nullCount')}</dt>
                <dd className="lq-pp__num">{count(sketch.null_count)}</dd>
                <dt>{t('profile.column.rows')}</dt>
                <dd className="lq-pp__num">{count(sketch.rows_scanned)}</dd>
                <dt>{t('profile.detail.precision')}</dt>
                <dd>
                  {sketch.exact
                    ? t('profile.detail.exact')
                    : t('profile.detail.estimated')}
                </dd>
              </dl>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/* A drift verdict as the design's chip. "Cannot say" wears the hollow dot: it is an absence of
 * measurement, and a filled dot would claim a reading. */
function DriftChip({
  verdict,
  estimated,
  dot = true,
}: {
  readonly verdict: DriftVerdict;
  readonly estimated: boolean;
  /** The changes list draws its chips without a dot, as the design does. */
  readonly dot?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <span
      className="lq-cn__state lq-pp__chip"
      data-tone={DRIFT_TONE[verdict]}
      title={t(`profile.drift.meaning.${verdict}`)}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className={
            verdict === 'unmeasurable' ? 'lq-cn__dot lq-cn__dot--hollow' : 'lq-cn__dot'
          }
        />
      ) : null}
      {t(`profile.drift.verdict.${verdict}`)}
      {estimated ? (
        /* The verdict was adjudicated against estimates, so only a large movement is reported. */
        <span title={t('profile.drift.estimated')}>~</span>
      ) : null}
    </span>
  );
}

function ChangesCard({
  drift,
  versionNumbers,
  onCompareRuns,
  onOpen,
}: {
  readonly drift: ReturnType<typeof useSketchDrift>;
  readonly versionNumbers: ReadonlyMap<string, number> | undefined;
  readonly onCompareRuns: (() => void) | undefined;
  readonly onOpen: (column: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const changed = drift.isSuccess
    ? (drift.data?.columns ?? []).filter((row) => driftVerdict(row) !== 'stable')
    : [];
  /* The drift is one schema version's latest profile against the previous version's, so the card
     names that version: "since the last profile" read as run to run, which is Profile history's
     question rather than this card's. */
  const previous =
    drift.isSuccess && drift.data !== null
      ? versionNumbers?.get(drift.data.baseline_version_id)
      : undefined;

  return (
    <section className="lq-pp__card" aria-labelledby="profile-changes-heading">
      <div className="lq-pp__cardHead lq-pp__cardHead--tight">
        <HelpLabel subject={t('profile.changes.title')} help={t('help.metric.drift')}>
          <h3
            id="profile-changes-heading"
            className="lq-pp__cardTitle"
            title={
              drift.isSuccess && drift.data !== null
                ? t('profile.drift.computedFrom', { how: drift.data.computed_from })
                : undefined
            }
          >
            {t('profile.changes.title')}
          </h3>
        </HelpLabel>
        {drift.isSuccess && drift.data !== null ? (
          <span className="lq-pp__count lq-pp__num">{changed.length}</span>
        ) : null}
      </div>
      {drift.isSuccess && drift.data !== null && (
        <p className="lq-pp__changesSub">
          {previous === undefined
            ? t('profile.changes.subNoVersion')
            : t('profile.changes.sub', { version: previous })}
        </p>
      )}
      {/* **Four states, and the first version collapsed three of them into a claim about the data.**
          A 404 genuinely means there is no baseline; a 500, a 429 or a still-pending request mean
          nothing about drift at all, and saying "nothing has drifted yet" for them reports a failure
          of the request as a fact about the table. */}
      {drift.isPending ? (
        <p className="lq-pp__sideNote" role="status">
          {t('profile.drift.loading')}
        </p>
      ) : drift.isError ? (
        <Alert tone="warning" title={t('profile.drift.failed')}>
          {drift.error.message}
        </Alert>
      ) : drift.data === null ? (
        <p className="lq-pp__sideNote">{t('profile.drift.none')}</p>
      ) : (
        <>
          {changed.length === 0 ? (
            <p className="lq-pp__sideNote">{t('profile.changes.none')}</p>
          ) : (
            <ul className="lq-pp__changes">
              {changed.map((row) => (
                <ChangeRow key={row.column} drift={row} onOpen={onOpen} />
              ))}
            </ul>
          )}
          <DriftFooter drift={drift.data} />
        </>
      )}
      {onCompareRuns !== undefined && (
        <div className="lq-pp__compareRuns">
          <button type="button" className="lq-pp__compareLink" onClick={onCompareRuns}>
            {t('profile.changes.compareRuns')}
          </button>
          <span className="lq-pp__compareNote">
            {t('profile.changes.compareRunsNote')}
          </span>
        </div>
      )}
    </section>
  );
}

function ChangeRow({
  drift,
  onOpen,
}: {
  readonly drift: ColumnDrift;
  readonly onOpen: (column: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const verdict = driftVerdict(drift);
  return (
    <li>
      <button
        type="button"
        className="lq-pp__change"
        onClick={() => {
          onOpen(drift.column);
        }}
      >
        <span className="lq-pp__changeHead">
          <span className="lq-pp__name">{drift.column}</span>
          <DriftChip verdict={verdict} estimated={!drift.exact} dot={false} />
        </span>
        <span className="lq-pp__changeDetail">{changeDetail(drift, t)}</span>
      </button>
    </li>
  );
}

/* What changed in this column, as the design words it: the completeness movement when there was
 * one, otherwise the verdict's own sentence. Values are never shown, so a replaced column says its
 * value set turned over rather than naming the new top value. */
function changeDetail(drift: ColumnDrift, t: TFunction): string {
  const before = drift.completeness_before ?? null;
  const after = drift.completeness_after ?? null;
  if (after === null && before !== null) return t('profile.change.nothingToMeasure');
  if (before !== null && after !== null && Math.abs(after - before) > 0.0005) {
    return t('profile.change.completeness', {
      before: share(before),
      after: share(after),
    });
  }
  return t(`profile.drift.sentence.${driftVerdict(drift)}`);
}

/* Columns that came or went between the two profiles — a schema change the verdicts above cannot
 * show, since a column on one side only has nothing to compare. Nothing at all when none did, which
 * is the usual case; how the comparison was made is on the heading's hover. */
function DriftFooter({ drift }: { readonly drift: SketchDrift }): ReactElement | null {
  const { t } = useTranslation();
  const parts = [
    drift.added_columns.length > 0
      ? t('profile.drift.added', { columns: drift.added_columns.join(', ') })
      : null,
    drift.removed_columns.length > 0
      ? t('profile.drift.removed', { columns: drift.removed_columns.join(', ') })
      : null,
  ].filter((part): part is string => part !== null);
  if (parts.length === 0) return null;
  return <p className="lq-pp__sideNote">{parts.join(' ')}</p>;
}

/* The design's "at a glance": four figures derived from the findings rather than hand-picked, and the
 * baseline's deductions one click away. See `profileHighlights` for why the figures are derived — the
 * platform does not know which of a customer's columns matters. A slot with no finding says so in
 * words rather than reading "—". */
function GlanceCard({ sketches }: { readonly sketches: DatasetSketches }): ReactElement {
  const { t } = useTranslation();
  const [why, setWhy] = useState(false);
  const measured = sketches.columns.filter(
    (each): each is ColumnSketch & { readonly completeness: number } =>
      each.completeness !== null,
  );
  const mean =
    measured.length === 0
      ? null
      : measured.reduce((sum, each) => sum + each.completeness, 0) / measured.length;
  const unmeasured = sketches.columns.length - measured.length;
  const baseline = sketches.baseline;

  return (
    <section className="lq-pp__card" aria-labelledby="profile-glance-heading">
      <h3 id="profile-glance-heading" className="lq-pp__cardTitle">
        {t('profile.glance.title')}
      </h3>
      <div className="lq-pp__glance">
        <div>
          <div className="lq-pp__glanceValue lq-pp__num">{share(mean)}</div>
          <div className="lq-pp__glanceLabel">{t('profile.glance.meanCompleteness')}</div>
        </div>
        <div>
          <div className="lq-pp__glanceValue lq-pp__num">{unmeasured}</div>
          <div className="lq-pp__glanceLabel">{t('profile.glance.unmeasured')}</div>
        </div>
      </div>
      <button
        type="button"
        className="lq-pp__why"
        aria-expanded={why}
        onClick={() => {
          setWhy(!why);
        }}
      >
        {why
          ? t('profile.glance.hideWhy')
          : baseline.value === null
            ? t('profile.glance.whyNone')
            : t('profile.glance.why', { value: baseline.value })}
      </button>
      {/* What the baseline was subtracted from, one line per deduction: the reason on the left and
          the points it cost on the right. A figure without the list is a number a steward cannot
          argue with. */}
      {why ? (
        baseline.value === null ? (
          <p className="lq-pp__sideNote">
            {t('profile.baseline.unmeasured', {
              columns: baseline.unmeasured_columns.length,
            })}
          </p>
        ) : baseline.findings.length === 0 ? (
          <p className="lq-pp__sideNote">{t('profile.baseline.nothingFound')}</p>
        ) : (
          <ul className="lq-pp__deductions">
            {baseline.findings.map((finding) => (
              <li key={`${finding.column}:${finding.kind}`}>
                <span className="lq-pp__deductionReason">
                  <span className="lq-mono">{finding.column}</span>{' '}
                  {t(`profile.finding.${finding.kind}`, { observed: finding.observed })}
                </span>
                <strong className="lq-pp__deductionPoints lq-pp__num">
                  {t('profile.baseline.deduction', { points: finding.deduction })}
                </strong>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}
